import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ParametrosEnum } from 'src/app/shared/enums/parameters.enum';
import { CuentaBeneficiario } from '../../../interfaces/cuenta-beneficiario.interface';
import { CuentaRetiro } from '../../../interfaces/cuenta-retiro.interface';
import { AccountRecipientService } from '../../../services/account-recipient.service';
import { ParameterService } from '../../../services/parameter.service';
import {
  VitaBeneficiario,
  VitaBeneficiarioService,
} from '../../../services/vita-beneficiario.service';
import { VitaCalculatorService } from '../../../services/vita-calculator.service';
import { WithdrawAccountService } from '../../../services/withdraw-account.service';
import AddAccountComponent from '../../account-recipient/components/add-account/add-account.component';
import { AddWithdrawAccountsComponent } from '../../add-withdraw-accounts/add-withdraw-accounts.component';
import { AddVitaBeneficiarioDialogComponent } from '../../vita-calculator-demo/components/add-vita-beneficiario-dialog/add-vita-beneficiario-dialog.component';
import {
  BeneficiarioVM,
  DestinoRemesa,
  RielColombia,
  TipoLibreta,
} from '../models/remittance.model';
import * as R from '../rules/remittance-rules';

/**
 * Contexto necesario para resolver la libreta. Nótese que la libreta depende
 * del DESTINO, no del origen: pagar en CLP, VES o USDT hacia Venezuela usa
 * siempre la misma libreta.
 */
export interface ContextoBeneficiario {
  destino: DestinoRemesa;
  usuarioId: number;
  /**
   * Lo que RECIBE el beneficiario, en moneda destino. Sólo se usa en la
   * libreta de Venezuela, donde el techo de Pago Móvil se compara contra los
   * Bs recibidos — nunca contra el monto enviado.
   */
  recibe: number | null;
  /** Riel de Colombia. Define la variante Vita y por lo tanto el schema. */
  rielCo: RielColombia;
  /**
   * Usuario con perfil VE. El diálogo de alta de Venezuela le muestra solo
   * cuentas Ahorro y Corriente, igual que en las otras pantallas.
   */
  esUsuarioVe?: boolean;
}

/** Cómo terminó el alta de un beneficiario. */
export interface ResultadoAlta {
  /** El diálogo confirmó el guardado. `false` = el usuario canceló o falló. */
  guardado: boolean;
  /**
   * El beneficiario creado, identificado con CERTEZA contra la libreta previa.
   *
   * `null` cuando se guardó pero no se lo pudo identificar: quedó fuera del
   * filtro de la libreta (Pago Móvil apagado, otra variante de Vita) o el
   * backend todavía no lo devuelve. En ese caso no se selecciona nada — el
   * flujo termina en un envío de dinero y elegir al beneficiario equivocado es
   * peor que pedirle al usuario que lo elija de la lista.
   */
  beneficiario: BeneficiarioVM | null;
}

/**
 * Resuelve la libreta de beneficiarios y el alta de nuevos, para las TRES
 * fuentes que conviven en el sistema. El paso 2 no conoce ninguna de ellas:
 * pide `listar()` o `abrirAlta()` y recibe `BeneficiarioVM`.
 *
 * NO toca backend. Reutiliza los diálogos de alta existentes —que son
 * diálogos autónomos, no pantallas del flujo viejo— porque el de Vita es un
 * motor de formularios dinámicos validado contra el proveedor y rehacerlo
 * sería riesgo puro sin ganancia de producto.
 */
@Injectable({ providedIn: 'root' })
export class RemittanceBeneficiaryService {
  constructor(
    private dialog: MatDialog,
    private accountRecipient: AccountRecipientService,
    private withdrawAccount: WithdrawAccountService,
    private vitaBeneficiario: VitaBeneficiarioService,
    private vitaCalculator: VitaCalculatorService,
    private parameter: ParameterService,
  ) {}

  /** Qué libreta aplica al destino. */
  libretaDe(destino: DestinoRemesa): TipoLibreta {
    return R.DESTINOS[destino].libreta;
  }

  /**
   * Variante de Vita para el destino. Perú es siempre `pe`; Colombia depende
   * del riel elegido en el paso 1. Se compara SIEMPRE en minúsculas: Vita
   * publica las variantes así y el catálogo del front las trae en mayúsculas.
   */
  varianteDe(destino: DestinoRemesa, rielCo: RielColombia): string {
    return destino === 'PE' ? 'pe' : String(rielCo).toLowerCase();
  }

  // ───────────────────── Listar ─────────────────────

  async listar(ctx: ContextoBeneficiario): Promise<BeneficiarioVM[]> {
    switch (this.libretaDe(ctx.destino)) {
      case 'VENEZUELA':
        return this.listarVenezuela(ctx);
      case 'CHILE':
        return this.listarChile();
      case 'VITA':
        return this.listarVita(ctx);
      default:
        return [];
    }
  }

  /**
   * Libreta Venezuela. Dos reglas que no se pueden perder:
   *  1. Los parámetros de Pago Móvil se cargan ANTES que la libreta — alimentan
   *     el filtro (espejo de `FiatCalculatorComponent:156`).
   *  2. El techo se compara contra los Bs RECIBIDOS, no contra el enviado.
   */
  private async listarVenezuela(
    ctx: ContextoBeneficiario,
  ): Promise<BeneficiarioVM[]> {
    const params = await this.parametrosPagoMovil();
    const todos = await this.accountRecipient.getByUser(ctx.usuarioId);
    const visibles = R.filtrarPorPagoMovil(todos || [], params);

    return visibles.map((b) => this.mapVenezuela(b, ctx.recibe, params.montoMax));
  }

  /**
   * Libreta Chile = las cuentas de RETIRO del propio usuario (`cuentas_retiro`),
   * que es lo único que acepta `retiroVesClp`. La libreta de retiro admite
   * varios países: acá solo van las chilenas (sin `codigoPais` cuentan como
   * CL, igual que en la libreta única), porque se pagan en CLP.
   */
  private async listarChile(): Promise<BeneficiarioVM[]> {
    const cuentas = await this.withdrawAccount.getWithdrawAccounts();
    return (cuentas || [])
      .filter((c) => String(c.codigoPais || 'CL').toUpperCase() === 'CL')
      .map((c) => this.mapChile(c));
  }

  /**
   * Libreta Vita, filtrada por variante. Un beneficiario cargado para Bre-B
   * NO sirve para transferencia bancaria: son variantes distintas.
   */
  private async listarVita(ctx: ContextoBeneficiario): Promise<BeneficiarioVM[]> {
    const variante = this.varianteDe(ctx.destino, ctx.rielCo);
    const lista = await firstValueFrom(this.vitaBeneficiario.list(ctx.destino));

    return (lista || [])
      .filter((b) => (b.variante || '').toLowerCase() === variante)
      .map((b) => this.mapVita(b));
  }

  // ───────────────────── Alta ─────────────────────

  /**
   * Abre el diálogo de alta que corresponda.
   *
   * `previos` son los ids que ya estaban en la libreta. Hacen falta porque los
   * tres diálogos no informan lo mismo: el de Vita devuelve la entidad creada,
   * pero los de Venezuela (`add-account.component.ts:340`) y Chile
   * (`add-withdraw-accounts.component.ts:236`) devuelven sólo `true`. Para esos
   * dos, el nuevo se identifica por DIFERENCIA contra la libreta previa —no por
   * "el id más alto", que devuelve un beneficiario preexistente cuando el
   * recién creado no aparece en la lista.
   */
  async abrirAlta(
    ctx: ContextoBeneficiario,
    previos: Set<string | number> = new Set(),
  ): Promise<ResultadoAlta> {
    switch (this.libretaDe(ctx.destino)) {
      case 'VENEZUELA':
        return this.altaVenezuela(ctx, previos);
      case 'CHILE':
        return this.altaChile(ctx, previos);
      case 'VITA':
        return this.altaVita(ctx);
      default:
        return { guardado: false, beneficiario: null };
    }
  }

  private async altaVenezuela(
    ctx: ContextoBeneficiario,
    previos: Set<string | number>,
  ): Promise<ResultadoAlta> {
    const ref = this.dialog.open(AddAccountComponent, {
      data: {
        type: 'CREATE',
        cuenta: null,
        isVeUser: !!ctx.esUsuarioVe,
        // El diálogo valida el techo de Pago Móvil contra el monto en Bs de la
        // operación — es lo que RECIBE el beneficiario.
        montoBs: ctx.recibe,
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      ...this.tamano(620, 560),
    });

    // El diálogo sólo cierra con `true` si el guardado no lanzó.
    const ok = await firstValueFrom(ref.afterClosed());
    if (ok !== true) return { guardado: false, beneficiario: null };

    const lista = await this.listarVenezuela(ctx);
    return { guardado: true, beneficiario: this.elNuevo(lista, previos) };
  }

  /**
   * Chile. `preselectedCountry` no es decorativo: sin él el diálogo navega a
   * `/wallet/client/withdraw` después de guardar
   * (`add-withdraw-accounts.component.ts:237`) y se lleva al usuario fuera del
   * cotizador.
   */
  private async altaChile(
    ctx: ContextoBeneficiario,
    previos: Set<string | number>,
  ): Promise<ResultadoAlta> {
    const ref = this.dialog.open(AddWithdrawAccountsComponent, {
      data: { type: 'CREATE', cuenta: null, preselectedCountry: 'CL' },
      ...this.tamano(826, 800),
    });

    const ok = await firstValueFrom(ref.afterClosed());
    if (ok !== true) return { guardado: false, beneficiario: null };

    const lista = await this.listarChile();
    return { guardado: true, beneficiario: this.elNuevo(lista, previos) };
  }

  /**
   * El alta de Vita necesita el SCHEMA cargado: el formulario se genera en
   * runtime desde `calculator/rules/{variante}`. Sin schema no hay diálogo.
   */
  private async altaVita(ctx: ContextoBeneficiario): Promise<ResultadoAlta> {
    const variante = this.varianteDe(ctx.destino, ctx.rielCo);
    const schema = await firstValueFrom(this.vitaCalculator.getRules(variante));

    const ref = this.dialog.open(AddVitaBeneficiarioDialogComponent, {
      data: {
        countryCode: ctx.destino,
        variant: variante,
        variantLabel: this.etiquetaVariante(ctx.destino, ctx.rielCo),
        schema,
      },
      ...this.tamano(560, 480),
    });

    // Vita es el único que devuelve la entidad: identificación exacta.
    const creado: VitaBeneficiario | null = await firstValueFrom(ref.afterClosed());
    return creado
      ? { guardado: true, beneficiario: this.mapVita(creado) }
      : { guardado: false, beneficiario: null };
  }

  private etiquetaVariante(destino: DestinoRemesa, rielCo: RielColombia): string {
    if (destino === 'PE') return 'Perú';
    return rielCo === 'COBREB' ? 'Bre-B' : 'Transferencia bancaria';
  }

  // ───────────────────── Normalización ─────────────────────

  private mapVenezuela(
    b: CuentaBeneficiario,
    recibe: number | null,
    montoMax: number,
  ): BeneficiarioVM {
    const esPagoMovil = b.movilPay === 1;
    const bloqueado = R.beneficiarioPagoMovilBloqueado(b, recibe, montoMax);

    const badges: string[] = [];
    if (esPagoMovil) badges.push('Pago Móvil');
    if (b.banco?.nombre) badges.push(b.banco.nombre);

    return {
      id: b.cuentaBeneficiarioID as number,
      libreta: 'VENEZUELA',
      titulo: `${b.nombre} ${b.apellidos}`.trim(),
      subtitulo: b.numeroCuenta || b.numeroID || '',
      badges,
      bloqueado,
      motivoBloqueo: bloqueado
        ? `Pago Móvil no admite montos sobre ${this.miles(montoMax)} Bs. Elige otro beneficiario.`
        : null,
      raw: b,
    };
  }

  private mapChile(c: CuentaRetiro): BeneficiarioVM {
    const badges: string[] = [];
    if (c.banco) badges.push(c.banco);
    if (c.tipoCuenta) badges.push(c.tipoCuenta);

    return {
      id: c.id,
      libreta: 'CHILE',
      titulo: c.alias || c.numeroCuenta || 'Cuenta',
      subtitulo: c.numeroCuenta || '',
      badges,
      bloqueado: false,
      motivoBloqueo: null,
      raw: c,
    };
  }

  private mapVita(b: VitaBeneficiario): BeneficiarioVM {
    const baneado = b.vitaEstado === 'banned';
    const badges: string[] = [];
    if (b.bancoNombre) badges.push(b.bancoNombre);
    else if (b.varianteLabel) badges.push(b.varianteLabel);

    return {
      id: b.id,
      libreta: 'VITA',
      titulo: `${b.nombre} ${b.apellidos || ''}`.trim(),
      subtitulo: b.cuentaMask || b.numeroId || '',
      badges,
      bloqueado: baneado,
      motivoBloqueo: baneado
        ? 'Este beneficiario no está habilitado para recibir transferencias. Comunícate con un operador de Aguacate.'
        : null,
      raw: b,
    };
  }

  // ───────────────────── Helpers ─────────────────────

  /** Parámetros de Pago Móvil. Ante error, lo más restrictivo. */
  private async parametrosPagoMovil(): Promise<R.ParametrosPagoMovil> {
    try {
      const [global, banesco] = await Promise.all([
        this.parameter.getParameter(ParametrosEnum.PAGO_MOVIL_GLOBAL),
        this.parameter.getParameter(ParametrosEnum.PAGO_MOVIL_BANESCO),
      ]);
      const montoMax = await this.parameter.getPagoMovilMontoMax();
      return {
        global: Number(global?.valor) || 0,
        banesco: Number(banesco?.valor) || 0,
        montoMax: Number(montoMax) || R.PAGO_MOVIL_MONTO_MAX_FALLBACK,
      };
    } catch {
      // Sin parámetros no se puede saber si Pago Móvil está habilitado:
      // se asume apagado (sólo beneficiarios que no son Pago Móvil).
      return {
        global: 0,
        banesco: 0,
        montoMax: R.PAGO_MOVIL_MONTO_MAX_FALLBACK,
      };
    }
  }

  /**
   * El que no estaba antes. Los diálogos de Venezuela y Chile confirman que
   * guardaron pero no dicen qué, así que el nuevo se busca por diferencia.
   *
   * Si aparecen cero o más de uno, devuelve `null`: cero significa que el
   * recién creado no entra en esta libreta —lo esconde el filtro de Pago Móvil
   * o es de otra variante de Vita—, y más de uno significa que no se puede
   * saber cuál es. Adivinar acá selecciona la cuenta de otra persona en el paso
   * anterior a confirmar un envío.
   */
  private elNuevo(
    lista: BeneficiarioVM[],
    previos: Set<string | number>,
  ): BeneficiarioVM | null {
    const nuevos = lista.filter((b) => !previos.has(b.id));
    return nuevos.length === 1 ? nuevos[0] : null;
  }

  private tamano(anchoMax: number, anchoMin: number) {
    const movil = typeof window !== 'undefined' && window.innerWidth < 768;
    return {
      maxWidth: movil ? '95vw' : `${anchoMax}px`,
      minWidth: movil ? '90vw' : `${anchoMin}px`,
      maxHeight: '90vh',
    };
  }

  private miles(n: number): string {
    return Math.round(n).toLocaleString('es-CL');
  }
}
